import { useEffect, useState } from "react";
import { MarketplaceBrand } from "./marketplace_brand";
import {
  FlaskConical, ShieldCheck,
  LayoutDashboard, ReceiptText, WalletCards, Factory,
  Banknote, History, Package, Layers, Palette, Users,
  Settings, Lock, LogOut, X, TrendingUp, Warehouse, ScanLine,
  Trash2, ShoppingBag, AlertTriangle, Eye, Gauge, Wrench, Image, Globe, Boxes,
  Store, KeyRound, Database, Upload, Plug, ChevronDown
} from "lucide-react";

/**
 * Sidebar off-canvas (mobile-first).
 * Item & handler dipetakan dari parent. Tidak mengubah logika/permission.
 * Iter 67 — sekarang menerima `menu` custom + `roleLabel` + `showLock` untuk pakai ulang di Owner.
 */
const DEFAULT_MENU = [
  { group: "MENU UTAMA", items: [
    { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={18}/> },
    { id: "performance", label: "Performa Toko", icon: <TrendingUp size={18}/> },
    { id: "orders", label: "Pesanan", icon: <ReceiptText size={18}/> },
    { id: "scan", label: "Scan Nota", icon: <ScanLine size={18}/> },
    { id: "receivable", label: "Piutang", icon: <WalletCards size={18}/> },
    { id: "production", label: "Produksi", icon: <Factory size={18}/> },
    { id: "stock", label: "Stok", icon: <Warehouse size={18}/> },
  ]},
  { group: "KEUANGAN", items: [
    { id: "cash-closing", label: "Tutup Kas", icon: <Banknote size={18}/> },
    { id: "history", label: "Riwayat Pembatalan & Koreksi", icon: <History size={18}/> },
  ]},
  { group: "MASTER DATA", items: [
    { id: "master-products", label: "Master Produk", icon: <Package size={18}/> },
    { id: "master-materials", label: "Master Bahan", icon: <Layers size={18}/> },
    { id: "master-colors", label: "Master Warna", icon: <Palette size={18}/> },
  ]},
];

// Menu Owner/Admin: Menu Utama & Pemantauan selalu terlihat; grup lain accordion (collapsible).
export const OWNER_MENU = [
  { group: "MENU UTAMA", items: [
    { id: "dashboard", label: "Pusat Kendali", icon: <LayoutDashboard size={17}/> },
    { id: "orders", label: "Pesanan Offline", icon: <ReceiptText size={17}/> },
    { id: "shopee-monitor", label: "Pantau Produksi Shopee", icon: <ShoppingBag size={17}/> },
    { id: "offline-monitor", label: "Pantau Produksi Offline", icon: <Factory size={17}/> },
  ]},
  { group: "PEMANTAUAN KINERJA", items: [
    { id: "mockup-monitor", label: "Pantau Mockup", icon: <Eye size={17}/> },
    { id: "bordir-performance", label: "Performa Bordir", icon: <Gauge size={17}/> },
    { id: "performance", label: "Performa Toko", icon: <TrendingUp size={17}/> },
  ]},
  { group: "OPERASIONAL", collapsible: true, items: [
    { id: "production", label: "Produksi", icon: <Wrench size={17}/> },
    { id: "shopee", label: "Mockup Shopee", icon: <MarketplaceBrand channel="shopee" size="sm" label={false}/> },
    { id: "tokopedia", label: "Tokopedia", icon: <MarketplaceBrand channel="tokopedia" size="sm" label={false}/> },
    { id: "marketplace", label: "Pesanan Online", icon: <Globe size={17}/> },
    { id: "scan", label: "Scan Nota", icon: <ScanLine size={17}/> },
    { id: "stock", label: "Stok", icon: <Boxes size={17}/> },
    { id: "warehouse", label: "Pengeluaran Gudang", icon: <Warehouse size={17}/> },
    // Iter 538 — Panel HOLD/BATAL (Owner selalu; Rido approver dipromosikan via flag operator).
    { id: "hold-manager", label: "HOLD / Pembatalan Shopee", icon: <AlertTriangle size={17}/>, ownerOnly: true },
  ]},
  { group: "KEUANGAN", collapsible: true, items: [
    { id: "receivable", label: "Piutang", icon: <WalletCards size={17}/> },
    { id: "cash-closing", label: "Tutup Kas", icon: <Banknote size={17}/> },
    { id: "history", label: "Riwayat Pembatalan & Koreksi", icon: <History size={17}/> },
  ]},
  { group: "MASTER DATA", collapsible: true, items: [
    { id: "master-products", label: "Master Produk", icon: <Package size={17}/> },
    { id: "master-operators", label: "Master Operator", icon: <Users size={17}/> },
    { id: "master-materials", label: "Master Bahan", icon: <Layers size={17}/> },
    { id: "master-colors", label: "Master Warna", icon: <Palette size={17}/> },
  ]},
  { group: "PENGATURAN & SISTEM", collapsible: true, items: [
    { id: "workshop", label: "Info Workshop", icon: <Store size={17}/> },
    { id: "settings", label: "Pengaturan", icon: <Settings size={17}/> },
    { id: "change-password", label: "Ubah Password", icon: <KeyRound size={17}/> },
    { id: "ai-beta", label: "AI Beta (Read-Only)", icon: <FlaskConical size={17}/>, ownerOnly: true },
    { id: "order-no-check", label: "Asisten Nomor Pesanan", icon: <ShieldCheck size={17}/> },
    { id: "ai-eval-lab", label: "Lab Uji AI (Preview)", icon: <FlaskConical size={17}/>, ownerOnly: true, labOnly: true },
    // Sub-grup tertutup default; item ownerOnly difilter di App.js bila role !== "owner".
    { id: "maintenance", label: "Maintenance / Data", icon: <Database size={17}/>, children: [
      { id: "import-nota", label: "Impor Nota Lama", icon: <Upload size={16}/>, ownerOnly: true },
      { id: "shopee-connection", label: "Koneksi Shopee Sandbox", icon: <Plug size={16}/> },
      { id: "reset-nota", label: "Reset Semua Nota", icon: <Trash2 size={16}/>, ownerOnly: true, danger: true },
    ]},
  ]},
];

const OPEN_KEY = "ats-open-groups";
const readOpen = () => { try { return JSON.parse(localStorage.getItem(OPEN_KEY)) || {}; } catch { return {}; } };

function Badge({ id, b }) {
  if (typeof b === "number") return b > 0 ? <span className="ats-item-badge" data-testid={`admin-sidebar-badge-${id}`}>{b > 99 ? "99+" : b}</span> : null;
  return <>{(b.parts || []).filter(p => p.count > 0).map(p => <span key={p.tone} className={`ats-item-badge is-${p.tone}`} title={p.title}
    data-testid={`admin-sidebar-badge-${id}-${p.tone}`}>{p.count > 99 ? "99+" : p.count}</span>)}</>;
}

function Item({ item, current, onNavigate, badge, sub }) {
  return <button type="button" key={item.id}
    className={`ats-item${sub ? " ats-subitem" : ""} ${current === item.id ? "active" : ""}${item.danger ? " ats-item-danger" : ""}`}
    aria-current={current === item.id ? "page" : undefined}
    data-testid={`admin-sidebar-item-${item.id}`} onClick={() => onNavigate(item.id)}>
    <span className="ats-item-icon">{item.icon}</span>
    <span className="ats-item-label">{item.label}
      {badge && badge.hint && <small className="ats-item-hint" data-testid={`admin-sidebar-hint-${item.id}`}>{badge.hint}</small>}</span>
    {badge != null && <Badge id={item.id} b={badge}/>}
  </button>;
}

export function AdminTokoSidebar({ open, onClose, operatorName, current, onNavigate, onLock, onLogout, menu, roleLabel, showLock = true, menuBadges, compact = false }) {
  const [openGroups, setOpenGroups] = useState(readOpen);
  const toggle = (k) => setOpenGroups(prev => { const n = { ...prev, [k]: !prev[k] }; localStorage.setItem(OPEN_KEY, JSON.stringify(n)); return n; });
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const activeMenu = menu || DEFAULT_MENU;
  const label = roleLabel || "Admin Toko";

  return <>
    <div
      className={`ats-backdrop ${open?"open":""}`}
      data-testid="admin-sidebar-backdrop"
      onClick={onClose}
      aria-hidden={!open}
    />
    <aside
      className={`ats-panel ${open?"open":""}${compact?" ats-compact":""}`}
      data-testid="admin-sidebar"
      role="dialog"
      aria-label="Menu Admin Toko"
      aria-modal="true"
    >
      <div className="ats-head">
        <div className="ats-brand">
          <span className="ats-brand-title">TAMFARIS</span>
          <span className="ats-brand-sub">Be More Creative with Your Clothes.</span>
        </div>
        <button type="button" className="ats-close" data-testid="admin-sidebar-close" onClick={onClose} aria-label="Tutup menu">
          <X size={20}/>
        </button>
      </div>

      <div className="ats-user">
        <div className="ats-avatar" aria-hidden="true">{(operatorName||"A").slice(0,1).toUpperCase()}</div>
        <div className="ats-user-info">
          <b data-testid="admin-sidebar-user-name">{operatorName || "—"}</b>
          <small>{label}</small>
          <span className="ats-online"><span className="ats-dot"/> Online</span>
        </div>
      </div>

      <nav className="ats-nav" data-testid="admin-sidebar-nav">
        {activeMenu.map(section => {
          const hasActive = section.items.some(it => it.id === current || (it.children || []).some(c => c.id === current));
          const isOpen = !section.collapsible || !!openGroups[section.group];
          const slug = section.group.toLowerCase().replace(/[^a-z]+/g, "-");
          return <div className={`ats-section${section.collapsible ? " ats-collapsible" : ""}`} key={section.group}>
            {section.collapsible
              ? <button type="button" className={`ats-section-title ats-section-toggle${hasActive ? " has-active" : ""}`} aria-expanded={isOpen}
                  onClick={() => toggle(section.group)} data-testid={`admin-sidebar-group-${slug}`}>
                  <span>{section.group}</span><ChevronDown size={14} className={isOpen ? "is-open" : ""}/>
                </button>
              : <div className="ats-section-title">{section.group}</div>}
            {isOpen && section.items.map(item => item.children
              ? <div key={item.id} className="ats-subgroup">
                  <button type="button" className={`ats-item${item.children.some(c => c.id === current) ? " has-active" : ""}`} aria-expanded={!!openGroups[item.id]}
                    onClick={() => toggle(item.id)} data-testid={`admin-sidebar-item-${item.id}`}>
                    <span className="ats-item-icon">{item.icon}</span><span className="ats-item-label">{item.label}</span>
                    <ChevronDown size={14} className={`ats-chev${openGroups[item.id] ? " is-open" : ""}`}/>
                  </button>
                  {openGroups[item.id] && item.children.map(c => <Item key={c.id} item={c} current={current} onNavigate={onNavigate} sub/>)}
                </div>
              : <Item key={item.id} item={item} current={current} onNavigate={onNavigate} badge={menuBadges && menuBadges[item.id]}/>)}
          </div>;
        })}

        <div className="ats-section ats-section-akun">
          <div className="ats-section-title">AKUN</div>
          {showLock && <button
            type="button"
            className="ats-item"
            data-testid="admin-sidebar-item-lock"
            onClick={onLock}
          >
            <span className="ats-item-icon"><Lock size={18}/></span>
            <span className="ats-item-label">Kunci Aplikasi</span>
          </button>}
          <button
            type="button"
            className="ats-item ats-item-danger"
            data-testid="admin-sidebar-item-logout"
            onClick={onLogout}
          >
            <span className="ats-item-icon"><LogOut size={18}/></span>
            <span className="ats-item-label">Keluar</span>
          </button>
        </div>
      </nav>
    </aside>
  </>;
}
